import { circle, Units } from '@turf/turf';
import { ISpeciesCountData } from 'common/defines/analytic';
import { sizeByZoomLevel } from 'common/utils/util';
import { QUERY_KEY } from 'constants/constants';
import { useGetClientSettingMapView } from 'hooks/map-view/useGetClientSettingMapView';
import { useClientData } from 'hooks/useClientData';
import { useEffect, useMemo, useState } from 'react';
import { Layer, Source } from 'react-map-gl';
import { useQuery } from 'react-query';
import { getAllSpeciesCountAnalytic } from 'services/analytics/apiAnalyticsConfig.services';
import { useAppSelector } from 'store/hooks';
import { mapViewSelector } from 'store/slices/mapViewSlice';

export const useSpeciesCountAnalytics = () => {
  const [speciesCountData, setSpeciesCountData] = useState<ISpeciesCountData[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const {
    isLayer3D,
    isInEditTab,
    analyticId,
    mapViewState: { zoom },
    speciesCountDisplay,
  } = useAppSelector(mapViewSelector);
  const { getTopSurfaceOpacity, colorTransparent, isSameColorTopSurface, getOpacityExtrusion } =
    useGetClientSettingMapView();
  const { contourSetting } = useClientData();

  useQuery(
    [QUERY_KEY.GET_All_SPECIES_COUNT, currentPage],
    () =>
      getAllSpeciesCountAnalytic({
        analysisId: analyticId!,
        page: currentPage,
        perPage: 500,
      }),
    {
      enabled: !!analyticId,
      onSuccess(data) {
        const hasNextPage = data.hasNextPage;
        const docs = data.docs;
        if (hasNextPage) {
          setCurrentPage((prev) => prev + 1);
        }
        setSpeciesCountData((prev) => [...prev, ...docs]);
      },
    }
  );

  useEffect(() => {
    setSpeciesCountData([]);
    setCurrentPage(1);
  }, [analyticId]);

  const geoJsonData: GeoJSON.FeatureCollection = useMemo(() => {
    const featuresWithCirclePoint: GeoJSON.Feature[] = speciesCountData
      ?.filter((item) => {
        const foundSpecies = speciesCountDisplay.find((ele) => ele.key === item.speciesId);
        if (foundSpecies) {
          return foundSpecies.visible;
        }
        return false;
      })
      ?.map((item) => {
        let color = null;
        const foundSpecies = speciesCountDisplay.find((ele) => ele.key === item.speciesId);
        if (foundSpecies) {
          color = foundSpecies.color;
        } else {
          color = item.settingColor ?? item.defaultColor;
        }
        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [item.longX, item.latY],
          },
          properties: { ...item, color: color },
        };
      });

    const generateGeometryWithCircle = (item: ISpeciesCountData) => {
      const center = [item.longX, item.latY];
      const radius = sizeByZoomLevel(zoom) * 1000;
      let color = null;
      const foundSpecies = speciesCountDisplay.find((ele) => ele.key === item.speciesId);
      if (foundSpecies) {
        color = foundSpecies.color;
      } else {
        color = item.settingColor ?? item.defaultColor;
      }
      const options = {
        units: 'meters' as Units,
        properties: { ...item, color: color },
      };
      const circleGeometry = circle(center, radius, options);
      return circleGeometry;
    };

    return {
      type: 'FeatureCollection',
      features: isLayer3D
        ? speciesCountData?.map((item) => generateGeometryWithCircle(item))
        : (featuresWithCirclePoint as GeoJSON.Feature[]),
    };
  }, [isLayer3D, speciesCountData, speciesCountDisplay, zoom]);

  const layerSpeciesCount = useMemo(() => {
    if (!speciesCountData.length) return;

    return (
      geoJsonData &&
      !isInEditTab && (
        <Source id={`speciesCount-point`} type="geojson" data={geoJsonData}>
          {/* 2D Point */}
          <Layer
            id={`fill-top-surface-point`}
            type="circle"
            source={`speciesCount-circle-point`}
            paint={{
              'circle-color': ['get', 'color'],
              'circle-radius': 5,
            }}
            layout={{ visibility: !isLayer3D ? 'visible' : 'none' }}
          />
          {/* 3D */}
          <Layer
            id={`speciesCount-top-surface-point`}
            type="fill-extrusion"
            source={`speciesCount-circle-point`}
            paint={{
              'fill-extrusion-color': ['get', 'color'],
              'fill-extrusion-height': contourSetting,
              'fill-extrusion-base': contourSetting,
              'fill-extrusion-opacity': getTopSurfaceOpacity,
              'fill-extrusion-vertical-gradient': false,
            }}
            layout={{ visibility: isLayer3D ? 'visible' : 'none' }}
          />
          <Layer
            id={`speciesCount-side-surface-point`}
            type="fill-extrusion"
            source={`speciesCount-circle-point`}
            paint={{
              'fill-extrusion-color': isSameColorTopSurface ? ['get', 'color'] : colorTransparent,
              'fill-extrusion-height': contourSetting,
              'fill-extrusion-base': 0,
              'fill-extrusion-opacity': getOpacityExtrusion,
              // getOpacityExtrusion,
              'fill-extrusion-vertical-gradient': false,
            }}
            layout={{ visibility: isLayer3D ? 'visible' : 'none' }}
          />
        </Source>
      )
    );
  }, [
    colorTransparent,
    contourSetting,
    geoJsonData,
    getOpacityExtrusion,
    getTopSurfaceOpacity,
    isInEditTab,
    isLayer3D,
    isSameColorTopSurface,
    speciesCountData.length,
  ]);
  return { layerSpeciesCount };
};
